import React, { memo, useMemo } from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';
import type { DynamicToolUIPart, ToolUIPart, UITools } from 'ai';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import type { ActionRenderAi } from '@/ai/types/action-render-ai';
import { useThrottledValue } from '@/hooks/use-throttled-value';
import { StringRotation } from '../../StringRotation';
import AndieGetPostsByFilterTool from './AndieGetPostsByFilterTool';
import AndieGetPostsById from './AndieGetPostsById';
import AndieCreatePost from './AndieCreatePost';
import AndieUpdatePost from './AndieUpdatePost';
import AndieGetPrivateProfilesPublishTool from './AndieGetPrivateProfilesPublishTool';
import AndieGetPrivateProfileById from './AndieGetPrivateProfileById';
import AndieGeneratePostBriefingWorkflow from './AndieGeneratePostBriefingWorkflow';
import AndieBriefingToCopyWorkflow from './AndieBriefingToCopyWorkflow';
import AndieGeneratePlanningWorkflow from './AndieGeneratePlanningWorkflow';
import AndieBulkBriefingContentWorkflow from './AndieBulkBriefingContentWorkflow';
import AndieBriefingToImageWorkflow from './AndieBriefingToImageWorkflow';
import AndieLoadWidgetData from './AndieLoadWidgetData';
import AndieGetAvailableDashboards from './AndieGetAvailableDashboards';
import AndieGetDashboardDetailsById from './AndieGetDashboardDetailsById';
import AndieGetWidgetsAvailableMetrics from './AndieGetWidgetsAvailableMetrics';
import AndieGetWidgetsAvailableProfiles from './AndieGetWidgetsAvailableProfiles';
import AndieSearchAvailableWidgets from './AndieSearchAvailableWidgets';
import AndieSavePlanningAndGenerateBriefings from './AndieSavePlanningAndGenerateBriefings';
import AndieGetDailyInsightData from './AndieGetDailyInsightData';
import AndieGetPostInsightData from './AndieGetPostInsightData';
import AndieNavigateToRoute from './AndieNavigateToRoute';
import AndieGetAvailableRoutes from './AndieGetAvailableRoutes';
import AndieOpenPostToUserById from './AndieOpenPostToUserById';
import AndieSchedulePost from './AndieSchedulePost';
import AndieUnschedulePost from './AndieUnschedulePost';
import AndieDeletePost from './AndieDeletePost';
import AndieGetProfileBranding from './AndieGetProfileBranding';
import AndieUpdateProfileBranding from './AndieUpdateProfileBranding';
import AndieRegenerateProfileBranding from './AndieRegenerateProfileBranding';
import AndieSearchProfiles from './AndieSearchProfiles';
import AndieGetProfileByUsername from './AndieGetProfileByUsername';
import AndieGetWorkspaceCompetitors from './AndieGetWorkspaceCompetitors';
import AndieCreateWorkspaceCompetitor from './AndieCreateWorkspaceCompetitor';
import AndieGetProfileCompetitors from './AndieGetProfileCompetitors';
import AndieUpdateProfileCompetitors from './AndieUpdateProfileCompetitors';
import AndieSearchDashboardTemplates from './AndieSearchDashboardTemplates';
import AndieCreateDashboardFromTemplate from './AndieCreateDashboardFromTemplate';
import AndieOpenDemoDashboard from './AndieOpenDemoDashboard';
import AndieConnectFirstProfile from './AndieConnectFirstProfile';
import AndieWebSearch from './AndieWebSearch';
import AndieReadUrl from './AndieReadUrl';
import AndieSaveResearchBrief from './AndieSaveResearchBrief';

export type ToolPart = DynamicToolUIPart | ToolUIPart<UITools>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyToolComponent = React.ComponentType<ActionRenderAi<any, any>>;

const toolComponentMap: Record<string, AnyToolComponent> = {
    getPostsByFilterTool: AndieGetPostsByFilterTool,
    getPostsById: AndieGetPostsById,
    createPost: AndieCreatePost,
    updatePost: AndieUpdatePost,
    getPrivateProfilesPublishTool: AndieGetPrivateProfilesPublishTool,
    getPrivateProfileById: AndieGetPrivateProfileById,
    getProfileBranding: AndieGetProfileBranding,
    updateProfileBranding: AndieUpdateProfileBranding,
    regenerateProfileBranding: AndieRegenerateProfileBranding,
    searchProfiles: AndieSearchProfiles,
    getProfileByUsername: AndieGetProfileByUsername,
    getWorkspaceCompetitors: AndieGetWorkspaceCompetitors,
    createWorkspaceCompetitor: AndieCreateWorkspaceCompetitor,
    getProfileCompetitors: AndieGetProfileCompetitors,
    updateProfileCompetitors: AndieUpdateProfileCompetitors,
    webSearch: AndieWebSearch,
    readUrl: AndieReadUrl,
    saveResearch: AndieSaveResearchBrief,
    generatePostBriefingWorkflow: AndieGeneratePostBriefingWorkflow,
    briefingToCopyWorkflow: AndieBriefingToCopyWorkflow,
    briefingToImageWorkflow: AndieBriefingToImageWorkflow,
    generateOrEditImageWorkflow: AndieBriefingToImageWorkflow,
    generatePlanningWorkflow: AndieGeneratePlanningWorkflow,
    savePlanningAndGenerateBriefings: AndieSavePlanningAndGenerateBriefings,
    bulkBriefingContentWorkflow: AndieBulkBriefingContentWorkflow,
    getWidgetsDataById: AndieLoadWidgetData,
    getWidgetDataBySchema: AndieLoadWidgetData,
    getAvailableDashboards: AndieGetAvailableDashboards,
    getDashboardDetailsById: AndieGetDashboardDetailsById,
    getWidgetsAvailableMetrics: AndieGetWidgetsAvailableMetrics,
    getWidgetsAvailableProfiles: AndieGetWidgetsAvailableProfiles,
    getSearchAvailableWidgets: AndieSearchAvailableWidgets,
    searchDashboardTemplates: AndieSearchDashboardTemplates,
    createDashboardFromTemplate: AndieCreateDashboardFromTemplate,
    openDemoDashboard: AndieOpenDemoDashboard,
    connectFirstProfile: AndieConnectFirstProfile,
    getDailyInsightData: AndieGetDailyInsightData,
    getPostInsightData: AndieGetPostInsightData,
    navigateToRoute: AndieNavigateToRoute,
    getAvailableRoutes: AndieGetAvailableRoutes,
    openPostToUserById: AndieOpenPostToUserById,
    schedulePost: AndieSchedulePost,
    unschedulePost: AndieUnschedulePost,
    deletePost: AndieDeletePost,
};

// A tool part only ever leaves a non-terminal state when the run produces an
// output/error/denial. If the run already stopped (the user hit cancel, or it
// errored out) while a part is still non-terminal, that part will never settle
// — surface it as CANCELLED instead of leaving it pulsing forever.
function mapState(state: ToolPart['state'], isActive: boolean): `${ActionResponseStatusAi}` {
    switch (state) {
        case 'output-available':
        case 'output-error':
        case 'output-denied':
            return ActionResponseStatusAi.COMPLETE;
        default:
            return isActive ? ActionResponseStatusAi.EXECUTING : ActionResponseStatusAi.CANCELLED;
    }
}

export function getToolName(part: ToolPart): string {
    if ('toolName' in part) return part.toolName;
    return part.type.startsWith('tool-') ? part.type.slice(5) : part.type;
}

// Tools whose invocation must not render in the transcript: their real UI lives elsewhere (e.g.
// `askUser` is surfaced as the pause wizard above the composer), so the generic tool stub is noise.
export const HIDDEN_TOOL_NAMES = new Set<string>(['askUser']);

export function isHiddenToolPart(part: ToolPart): boolean {
    return HIDDEN_TOOL_NAMES.has(getToolName(part));
}

// The heavy render path. Memoized so it only re-runs when the (throttled) part
// reference actually changes, not on every streamed token.
const AndieToolInvocationInner = memo(function AndieToolInvocationInner({
    part,
    isActive,
}: {
    part: ToolPart;
    isActive: boolean;
}) {
    const toolName = getToolName(part);
    // Hidden tools (e.g. askUser) render their own UI elsewhere; never show the generic stub.
    if (HIDDEN_TOOL_NAMES.has(toolName)) return null;
    const status = mapState(part.state, isActive);
    const Component = toolComponentMap[toolName];

    const isError = part.state === 'output-error' || part.state === 'output-denied';
    const actionProps: ActionRenderAi<unknown, unknown> = {
        status,
        result: isError
            ? { code: part.state, message: part.errorText || 'Tool execution failed' }
            : part.output,
        args: part.input,
    };

    if (Component) {
        return <Component {...actionProps} />;
    }

    return (
        <Stack direction="column" alignItems="left" spacing={1}>
            <Tooltip title={toolName}>
                <Typography variant="body2" color="textSecondary">
                    {status === ActionResponseStatusAi.CANCELLED ? (
                        'Andie cancelled this action.'
                    ) : status === ActionResponseStatusAi.EXECUTING ? (
                        <>
                            Andie is executing an action in the system
                            <StringRotation messages={['.', '..', '...']} intervalMs={500} fadeMs={1} />
                        </>
                    ) : (
                        'Andie received the result from the system.'
                    )}
                </Typography>
            </Tooltip>
        </Stack>
    );
});

export default function AndieToolInvocation({
    part,
    isActive = true,
}: {
    part: ToolPart;
    // Whether the run this tool belongs to is still in progress. When false, a
    // non-terminal tool part is treated as cancelled rather than executing.
    isActive?: boolean;
}) {
    // Tool args (`input`) stream in token-by-token and drive the heavy render
    // path. Throttle only the input so downstream components update ~10x/sec
    // instead of per char. State/output are passed through live so tools that
    // gate side-effects on the EXECUTING transition never miss it. Once settled,
    // use the final input directly so no trailing args are dropped.
    const isSettled =
        part.state === 'output-available' || part.state === 'output-error' || part.state === 'output-denied';
    const throttledInput = useThrottledValue(part.input, 100);
    const effectiveInput = isSettled ? part.input : throttledInput;

    const throttledPart = useMemo(
        () => ({ ...part, input: effectiveInput }) as ToolPart,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [part.state, part.output, part.errorText, part.toolCallId, effectiveInput],
    );

    return <AndieToolInvocationInner part={throttledPart} isActive={isActive} />;
}
